/*eslint eqeqeq: "off"*/
/**
 * VENDOR MODULES
 */
import React from 'react';
import i18n from 'i18n';

import { printOdd } from 'shared/utils/common';

import EVENT_NAMES                 from 'shared/constants/events';
import Emitter                     from 'shared/events/emitter';
import { getMarketTypeByGroupId }  from 'shared/utils/markets';

import {
  pickOutcome,
  checkOutcomeInTicket,
} from 'shared/utils/ticket-slip-helper';

import { useSchedulesQuery } from 'features/events/queries';
import { useScheduleContext } from 'features/events/context/ScheduleContext';
import { useSchedulePanelContext } from 'features/events/SchedulePanelContext';
import { useSelectedEventForSchedule } from 'features/events/hooks/useSelectedEventForSchedule';
import { useTicketSlipStore } from 'features/tickets/store';
import { UserSessionContext } from 'features/session/userSessionStore';
import { cn } from 'lib/utils';
import { getSportTextColorClasses } from 'shared/utils/sport-colors';
import { useHasEventStarted } from 'shared/hooks/useHasEventStarted';

/**
 * Odd for grid layout
 *
 * @class      GridOdd (name)
 * @param      {object}  props   The properties
 * @return     {DOM}
 */
function GridOdd(props) {
  const {
    outcome,
    outcome_id,
    market_type_group_id,
    market_type_id,
    event_id,
    odd,
    class_names = [],
    display_label,
    event: eventProp,
  } = props;

  const add_class_names           = class_names.join(' ');
  const hasCustomBgClass = /\bbg-/.test(add_class_names) || add_class_names.includes('bg[');
  const isFootball3dOdd = add_class_names.includes('grid-odd-football3d');
  const { data: schedules = [] } = useSchedulesQuery();
  const ticket_slip = useTicketSlipStore((s) => s.ticket_slip);
  const addTip = useTicketSlipStore((s) => s.addTip);
  const selected_event = useScheduleContext().selectedEvent || {};
  const panelCtx = useSchedulePanelContext();
  const panelEvent = useSelectedEventForSchedule(panelCtx?.scheduleId ?? null);
  const [{ user }] = React.useContext(UserSessionContext);
  const eventForBet =
    eventProp ||
    (panelCtx ? (panelEvent ?? {}) : selected_event);
  const { sport_id } = eventForBet || {};
  const odds_format = user?.odds_format || 'decimal';
  const hasStarted = useHasEventStarted(eventForBet?.event_timestamp);

  // Global sport theme variables are set at app root.
  const sportColor = 'var(--sport-color)';
  const textColorSport = getSportTextColorClasses(sport_id);
  const focusRingColor = 'var(--sport-color)';

  // Check if this outcome is already in the ticket slip
  const isInTicket = checkOutcomeInTicket(outcome_id ?? outcome, ticket_slip);

  const onClick = () => {
    if (!odd || hasStarted) {
      return false;
    }

    const {name: market_name} = (getMarketTypeByGroupId(market_type_group_id, eventForBet) ?? {});

    // Get sport alias from schedules - find the schedule with matching sport_id
    const scheduleWithSport = Array.isArray(schedules)
      ? schedules.find(s => s.sport?.sport_id == sport_id)
      : null;
    const sport_alias = scheduleWithSport?.sport?.alias || '';
    const tip = {
      ...pickOutcome({
        event: eventForBet,
        pick_outcome: outcome,
        market_type_id: market_type_id,
      }),
      stake: 1.00,
      market_type_group_id: market_type_group_id,
      market_name: market_name,
      sport_alias: sport_alias,
    };

    /**
     * validations
     */
    if (checkOutcomeInTicket(tip.outcome_id, ticket_slip)) {
        Emitter.emit(EVENT_NAMES.DIALOG.SHOW, {
          title: i18n.t('DOUBLE_BET'),
          message: i18n.t('EXISTING_OUTCOME'),
        });
        return false;
    }

    addTip(tip);
  };

  // Calculate shadow color with opacity
  const shadowColor = 'rgba(0,0,0,0.12)';

  return (
    <button
      className={cn(
        add_class_names,
        display_label ? "flex flex-col" : "flex items-center",
        "justify-center cursor-pointer transition-all",
        "border rounded-md",
        "min-h-[44px]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sport-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        // Only add default padding if no custom padding class is provided
        !add_class_names.includes('p-') && "p-2",
        // Active/Selected (in ticket): Sport-specific color
        isInTicket && (isFootball3dOdd ? "text-[var(--sport-on-color)] border-transparent" : `${textColorSport} border-transparent`),
        // Default state (has odds, not in ticket): allow caller to override bg-* so sport tint can show through
        !isInTicket && odd && !hasStarted && [
          !hasCustomBgClass && "bg-background",
          "text-foreground border-border hover:bg-[var(--sport-color-hover)] hover:text-[var(--sport-on-color)] hover:border-transparent",
        ],
        // Disabled while running
        odd && hasStarted && [
          !hasCustomBgClass && "bg-background",
          "text-muted-foreground border-border opacity-60 cursor-not-allowed",
        ],
        // Inactive (no odds): Sport-specific color
        !odd && `${textColorSport} border-transparent opacity-50 cursor-not-allowed`
      )}
      style={{
        ...(isInTicket ? {
          // Football3D: active should match hover styling
          backgroundColor: isFootball3dOdd ? 'var(--sport-color-hover)' : 'var(--sport-color)',
          borderColor: 'transparent',
          boxShadow: `0 10px 15px -3px ${shadowColor}, 0 4px 6px -2px ${shadowColor}`
        } : {}),
        ...(!odd ? {
          backgroundColor: 'var(--sport-color)',
          borderColor: 'transparent'
        } : {}),
        ...(odd && hasStarted ? {
          boxShadow: 'none'
        } : {})
      }}
      data-outcome={outcome}
      data-market-type-group-id={market_type_group_id}
      data-market-type-id={market_type_id}
      data-event-id={event_id}
      data-odd={odd}
      onClick={onClick}
      disabled={!odd || hasStarted}
      aria-label={`Bet on outcome ${display_label || outcome} with odds ${odd || 'N.Q.'}`}
    >
      {display_label && (
        <span className="text-xs font-medium mb-0.5 leading-tight">{display_label}</span>
      )}
      <span className="font-semibold">{odd? printOdd(odd, odds_format): 'N.Q.'}</span>
    </button>
  );
}

export default GridOdd;

